/**
 * App bootstrap. Wires the eager api/dispatch/push-stream callback injections (with the seam
 * adapters — lazy-game-graph-boot-seam design), the fetch-render router + pristine-boot
 * game-start renderer, the URL-flag-gated dev tools (lazy-imported, no-op unless their flag is
 * present), and init() — the module load sequence from script-tag entry (skat.js) through the
 * first render and the boot overlay's dissolve.
 *
 * The game graph itself (game.js, the animation controller/dispatch, the bid pipeline,
 * board-layout.js) is NOT imported here — it lives behind game-graph.js#ensureGameUi, a dynamic
 * import that resolves at Play, at the menu-button click, or (dev-only) when a flag below forces
 * it early. This module's callbacks are eager precisely so an unwired window can never be a
 * silent no-render (api.js's onBeforeApi/onAfterApi/onFetchRender are null-guarded skips).
 */
import { loadMessages } from "./i18n.js";
import { initTooltips } from "./tooltip.js";
import { initMenuHotkey } from "./menu-button.js";
import { sweepLegacyStorage } from "./legacy-storage-sweep.js";
import {
  beginStateFetch,
  renderFetchedState,
  getGame,
  applyServerState,
  fetchState,
  initApi,
  initChainRest,
  api,
  getCurrentGame,
} from "./api.js";
import {
  initPushStream,
  ensureOpen as ensurePushStreamOpen,
} from "./push-stream.js";
import { decodeWireState } from "./serde/decode-wire-state.js";
import { setSessionConfig } from "./session-config.js";
import {
  maybeDrainCardQueue,
  initCardQueue,
  resetCardQueue,
} from "./card-queue.js";
import { clearHintTimer } from "./hints.js";
import { isDevMode } from "./dev-mode.js";
import {
  awaitAppReady,
  dissolveAppLoader,
  pinBootSplash,
} from "./app-loader.js";
import {
  setApp,
  getApp,
  setCurrentState,
  setBoardBurstShaped,
  hadApiError,
  setHadApiError,
  setAnimating,
} from "./state.js";
import { ensureGameUi, getGameUi } from "./game-graph.js";
import { installStaleClientReload, checkStaleClient } from "./stale-client.js";
import {
  renderGameStart,
  resetFreshnessGates,
} from "./game-start/game-start-ui.js";
import { gsap } from "./vendor/gsap.js";

// The e2e settle guard's handle on the page's own GSAP schedule (tests/e2e/helpers/layout.js —
// settledBoxes / collectCardPolygons wait for the global timeline to go idle). They used to reach
// it with a same-URL `import("/js/vendor/gsap.js")`, which binds the page's real instance only on
// a source-booted page; on a bundled page (prod, the built image, the nightly's built e2e leg)
// nothing is emitted under /js/*, so that import falls through to a detached classpath copy and
// the guard silently goes inert. Exposing the timeline — not the gsap object: it is all either
// consumer reads — makes the handle world-independent. boot.js is the authoritative boot tail on
// every surface that animates the board (the game page and /test-scenarios both), and gsap is
// already in the eager graph, so this changes no load ordering.
// why: specs/2026-08/2026-08-24-build-step-nightly-built-e2e-design.md § 4
globalThis.__appGsapTimeline = gsap.globalTimeline;

// Exported so the wiring itself is testable (initAnimationLockCallbacks precedent) — a unit test
// calls this directly with initApi/initChainRest/initPushStream mocked, then invokes the captured
// callbacks to exercise the seam adapters in isolation from the rest of init()'s font-gate/i18n
// sequencing.
export function initApiCallbacks() {
  initApi(
    (endpoint, params) => {
      // onBeforeApi — sync by contract (pre-request snapshot); a null snapshot pre-graph is
      // correct, there's no board to snapshot yet (the deal render below rides onAfterApi, not
      // this callback, so Play never depends on the graph being warm here).
      clearHintTimer();
      return getGameUi() != null
        ? getGameUi().captureSnapshot(endpoint, params)
        : null;
    },
    async (state, action, params, snapshot) => {
      // onAfterApi — already awaited by applyServerState. The structural guarantee for the action
      // path: a deal response cannot render before the graph, cold-Play included.
      const ui = await ensureGameUi();
      return ui.renderWithAnimation(state, action, params, snapshot);
    },
    routeFetchRender, // onFetchRender — the router below
  );
  initChainRest(maybeDrainCardQueue); // chain rests at the human's card play → queued head may fire
  // The ingest's own callback injection (push-stream.js's header documents why: a direct
  // import back would cycle) — a walk message decodes and applies through the same funnel
  // every response does, tagged "continue" like the retired postContinue's call shape;
  // fetchState is the resync authority for both the gap-timeout and the terminal-close paths.
  initPushStream(
    (rawState) =>
      applyServerState(decodeWireState(rawState), "continue", {}, null),
    fetchState,
  );
}

/**
 * The fetch-render router (lazy-game-graph-boot-seam design) — hosted here rather than
 * game-graph.js so the import stays acyclic (game-graph.js is imported by game-start-ui.js, which
 * this module also imports for renderStartScreen). Exported for direct unit testing of its three
 * branches (pristine / warm-dirty / tail re-check).
 */
export async function routeFetchRender(state) {
  if (!state.phase && getGameUi() == null) {
    // Pristine boot: a run-bound table's own board owns its deal (multiplayer-surface design §
    // Entering a run — the board deals) — a phase-less state without the flag keeps today's solo
    // start screen.
    return state.multiplayerRun
      ? dealBoardBootRound()
      : renderStartScreen(state);
  }
  const ui = await ensureGameUi(); // resolved-instantly when warm — but still a microtask yield
  if (ui.isDispatchTailBusy()) return; // re-check after the await, via the resolved handle (the
  // check lives in the lazy graph): work claiming the tail on another promise chain during the
  // yield owns the DOM — api.js's onFetchRender invariant re-asserted past the async boundary
  // (round-2 review finding). Widened with that invariant (resync-render-lands-mid-bid-replay):
  // a burst merely QUEUED behind the round-start replay owns the DOM just as much as a draining
  // one, and this render would be overwritten by it either way — including the teardown-render
  // cost api.js#renderFetchedState's own doc records, which this guard shares by construction.
  // Returned rather than fire-and-forget (tbt-reshaping slice 1 §2): this is also
  // the warm-dirty dev-flag boot path (?renderDebug/?animScale/?scrub/?txEager forcing the graph
  // before the first render), where introHasPlayed is still false — its render() call is the one
  // that actually runs the chunked entrance build, so renderFetchedState's existing await must
  // hold the boot overlay through it the same way the pristine path does.
  return ui.render(state);
}

/**
 * The pristine case only — a `!state.phase` render with the game graph never loaded, i.e. a fresh
 * boot onto the start screen. The rest of game.js#render's prologue (resetBidStateForDeal,
 * clearAppForRender's bloom/discard/layout teardown) is provably no-op on a pristine app — nothing
 * has built the state it clears, so it isn't reproduced here. Every non-pristine render — Quit-from-
 * live included — reaches routeFetchRender with a warm gameUi and delegates to game.js#render
 * unchanged instead.
 *
 * Returns renderGameStart's entrance-build promise (tbt-reshaping slice 1 §2) so routeFetchRender's
 * caller (renderFetchedState) can hold the boot overlay through the chunked build, same as the
 * warm-dirty branch above.
 */
function renderStartScreen(state) {
  setCurrentState(state);
  setBoardBurstShaped(false);
  resetCardQueue();
  const sessionLost = hadApiError();
  setHadApiError(false);
  resetFreshnessGates(getApp());
  // The genuine first paint — the one call where the shell's data-your-turn-runs stamp is still
  // fresh (game-start-turn-banners.js's file header carries the why this can't be inferred from
  // call order: a resumed-live-game boot never reaches this function at all until a later Quit).
  return renderGameStart(sessionLost, true);
}

/**
 * Board-boot's own deal for a fresh multiplayer table (multiplayer-surface design § Entering a
 * run — the board deals): deals through the action funnel exactly like solo's first deal
 * (game-start/game-start-session.js#startFirstRound is the template — arm the entrance, then the
 * same `api("next-round", {})` call), so round 1 arrives with the full deal choreography rather
 * than a cold render of an undealt board. `entering from /multiplayer is just navigation` (§
 * Decision) — this is what actually deals.
 *
 * The bidding-stage entrance is dynamic-imported, not statically — boot.js's own header rule: the
 * game graph (and its animation modules) is never a static import here, only ever reached through
 * game-graph.js#ensureGameUi, which api()'s own onAfterApi callback (initApiCallbacks above)
 * already awaits.
 *
 * A losing two-member race self-heals through api()'s own structured recovery — a losing
 * next-round answers 400 "A round is still in progress" (resumeLiveRound's own documented
 * response), which api() folds into recoverFromApiError's fetchState re-fetch-and-render, landing
 * the loser mid-round with no new error handling here.
 *
 * Two separate try/catch blocks, deliberately not one (review finding — the merged shape looped):
 * a failed entrance import degrades to a plainer deal rather than failing the round at all — it's
 * a client-side/network hiccup, not a server race, so the api()-failure recovery below (fetchState
 * re-fetch) would just re-fetch the same still-undealt state and retry the whole import forever, a
 * genuinely unbounded loop for a permanently-broken chunk (a fetch-failed ES module import never
 * re-attempts the network fetch on a later import of the same URL). The api() call keeps its own
 * catch, covering the one reject source it doesn't itself swallow: a failed dynamic import of the
 * game graph on this first, cold deal (the same cold-Play race startFirstRound's own catch
 * documents) — that recovery is unchanged from before this file's board-boot addition.
 */
async function dealBoardBootRound() {
  try {
    const { armBiddingStageEntrance } =
      await import("./animation/bidding-stage-entrance.js");
    armBiddingStageEntrance();
  } catch (e) {
    // Degrade, don't fail the deal: the round below still deals through the funnel, just without
    // this first round's etch choreography. Never routes through the api()-failure recovery —
    // that path re-fetches and retries the deal, which would spin forever on an import that can
    // never succeed (review finding).
    console.warn(
      "[boot] bidding-stage entrance failed to load, dealing without it:",
      e?.message ?? e,
    );
    // A swallowed import failure is exactly where a stale client would otherwise keep limping in
    // degraded mode forever — probe, and reload if the entry URL confirms a deploy has landed.
    checkStaleClient();
  }
  try {
    await api("next-round", {});
  } catch (e) {
    console.error("[boot] board-boot deal failed:", e?.message ?? e);
    setHadApiError(true);
    setAnimating(false);
    await fetchState();
    return;
  }
  ensurePushStreamOpen(getCurrentGame());
}

async function maybeInitDevOverlay() {
  if (!isDevMode()) return;
  try {
    const mod = await import("./dev/dev-overlay.js");
    mod.initDevOverlay();
  } catch (e) {
    // Prod gates /js/dev/** off (go-live 404) — a ?dev flag on a prod URL must not break boot.
    console.warn("[dev-overlay] failed to load:", e?.message ?? e);
  }
}

// Lazy-import the chain pause control when ?paused=true (render-debug pattern).
// Must wire before the render below opens the stream (initPushStream's chain gate,
// hosted on push-stream.js since the swap) — the first walk message it might hold is
// exactly the case the gate exists for.
async function maybeInitChainPause() {
  if (new URLSearchParams(location.search).get("paused") !== "true") return;
  try {
    const mod = await import("./dev/chain-pause.js");
    mod.initChainPause();
  } catch (e) {
    // Prod gates /js/dev/** off (go-live 404) — a ?paused flag on a prod URL must not break boot.
    console.warn("[chain-pause] failed to load:", e?.message ?? e);
  }
}

// The URL-flag-gated dev tools that are graph-independent — each a no-op unless its flag is
// present. render-debug and the choreography scrubber moved into game-boot.js#initGameGraph (they
// need the graph to observe anything); init() below force-ensures the graph early when their
// flags are present, at the same position the old inline render-debug hoist used.
async function maybeInitDevTools() {
  await maybeInitDevOverlay();
  await maybeInitChainPause();
}

/**
 * Fires GET /api/games/{id}/session-info without blocking the caller — session-info
 * (hintsEnabled/autoPlay/gameMode) races the state read in parallel rather than adding a round
 * trip to boot (GameStateDto stopped echoing this config; scheduleHint et al. read
 * session-config.js instead). Returns the in-flight promise so init() can await it before the
 * first render — the config-before-render guarantee every consumer (scheduleHint, renderHintButton)
 * depends on. No stored handle at all (fresh visit, no game) rejects via getGame's own guard —
 * caught below exactly like any other failure, since a game-start boot has no session-info to
 * fetch anyway.
 */
function fetchSessionInfo() {
  return getGame("session-info")
    .then(setSessionConfig)
    .catch((e) => {
      console.warn(
        "[session-config] session-info fetch failed:",
        e?.message ?? e,
      );
    });
}

/** The page-global delegated listeners, installed once each: every surface this entry serves (the
 *  start screen, the board, /test-scenarios) wants all three, and none of them is tied to a screen
 *  that mounts or unmounts. Grouped so init()'s boot sequence reads as beats rather than wiring. */
function installDelegatedListeners() {
  initCardQueue(); // document-level click-anywhere reset (queue spec reset rule 3)
  initTooltips(); // document-level delegated tooltip listeners (js/tooltip.js)
  initMenuHotkey(); // Escape opens the menu, on whichever surface mounted a trigger (menu-button.js)
}

export async function init(appElement) {
  // First, before anything below can reach a render: the first render is what toggles
  // html.game-start-open, and the splash's vw anchor has to be frozen in px before that
  // happens or a resumed-game boot slides it mid-dissolve (app-loader.js#pinBootSplash).
  pinBootSplash();
  // Right after the pin, before init() starts awaiting anything (messages, dev tools, the
  // app-ready gate): the listeners are passive and cost nothing before first paint, and
  // installing here covers the whole window in which a stale client could reach a lazy import.
  installStaleClientReload();
  sweepLegacyStorage(); // one-time dead client-storage cleanup — keys nothing reads, plus the legacy browserId cookie
  setApp(appElement);
  installDelegatedListeners();

  initApiCallbacks();

  // why: start the boot state GET now — before the i18n fetch, the font gate, and the dev-only
  // render-debug hoist below — so it flies parallel to loadMessages on the first module tick
  // instead of stacking behind both (audit finding B2). Only the network runs here; the render is
  // held to renderFetchedState below (after loadMessages), because the first render calls t().
  const statePromise = beginStateFetch();
  // why: park the rejection. If the server is unreachable, `await loadMessages()` below throws and
  // init rejects before we reach `await statePromise` — leaving it a dangling rejected promise (a
  // spurious unhandledrejection). This no-op marks it handled; the real await in the try still sees
  // the rejection and propagates it, so error behaviour is unchanged.
  statePromise.catch(() => {});

  // why: start the session-info sync here, parallel to the state GET above, so both fly during the
  // i18n fetch and the font gate rather than starting cold. The first render awaits this before
  // painting (below) — config-before-render must be universal, else a hints-off session's first
  // frame schedules hints off the stale hintsEnabled:true module default (the zero-delay learning
  // path reveals a hint despite hints-off). Hoisting the start makes the added first-paint latency
  // max(0, sessionInfo − state) ≈ 0: session-info is an in-memory read, strictly cheaper than the
  // state GET that already gates paint. fetchSessionInfo swallows its own rejection, so no park.
  const sessionInfoPromise = fetchSessionInfo();

  // Dev flags force the game graph early (game-graph.js#ensureGameUi): ?renderDebug's capture and
  // the choreography scrubber's flags (?animScale/?freezeChoreography/?scrub) both depend on the
  // graph existing before their own observation windows open — their actual dev-tool imports moved
  // into game-boot.js#initGameGraph (this module can't import them without re-introducing the
  // eager edge). ?txEager additionally decides WHEN relative to loadMessages, exactly as it did for
  // the old inline render-debug hoist — before when set, after otherwise.
  const params = new URLSearchParams(location.search);
  const txEager = params.get("txEager") === "true";
  const forceGameGraph =
    txEager ||
    params.get("renderDebug") === "true" ||
    params.get("animScale") != null ||
    params.get("freezeChoreography") != null ||
    params.get("scrub") != null;

  if (txEager) await ensureGameUi();
  await loadMessages();
  if (!txEager && forceGameGraph) await ensureGameUi();
  await maybeInitDevTools();

  // Hold the boot overlay over the font gate, then dissolve it into the game-start
  // entrance: awaitAppReady gates on document.fonts.ready + min dwell (the slow part
  // the overlay covers), renderFetchedState's onFetchRender fires the first render +
  // entrance (fed by the state GET already in flight since init entry), and
  // dissolveAppLoader crossfades the overlay out as the inscription begins. All no-op
  // when the overlay is absent (dev/lab/scenario pages). See app-loader.js.
  await awaitAppReady();

  // dissolveAppLoader in finally: if the boot fetch throws (server unreachable at
  // load), the overlay must still clear — otherwise it strands as an eternal
  // loading bar. The error still propagates; only the cleanup is guaranteed.
  try {
    // Sync session-config before the first render so no frame paints off the stale module default
    // (both surfaces that reach init — a `/` reload and a `/test-scenarios` load — inherit the
    // guarantee). Awaited here rather than after render: the sync's already in flight since init
    // entry, so this joins a resolved (or near-resolved) promise, not a fresh round trip.
    await sessionInfoPromise;
    // Render the state now that messages are loaded — first render + game-start entrance fire
    // here, fed by the GET in flight since init entry rather than a fetch started at this line.
    // A parked display pause (AWAIT_AI_CARD / AWAIT_NEXT_TRICK / …) no longer needs resuming
    // from here: the server never parks on one post-swap, and the stream connect this render
    // triggers (renderFetchedState → ensurePushStreamOpen) walks a legacy/restored session's
    // pause itself (GameStreamController#connect's own trigger). See docs/DOM.md.
    // Awaited (renderFetchedState is now async, awaiting onFetchRender/routeFetchRender itself) so
    // dissolveAppLoader below genuinely holds the overlay through a cold reload-into-live-game,
    // rather than dissolving into a blank board while the graph is still loading.
    await renderFetchedState(await statePromise);
  } finally {
    dissolveAppLoader();
  }
}
